import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import SortableItem from '../admincomponents/SortableItem';

const apiUrl = process.env.REACT_APP_API_URL;

function AdminAccreditations() {
  const [accreditations, setAccreditations] = useState([]);
  const [title, setTitle] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAccreditations();
  }, []);

  const fetchAccreditations = async () => {
    setLoading(true);
    setError('');
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) {
        setError('You must be logged in to manage accreditations.');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${apiUrl}/api/admin/accreditations`, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });
      if (response.data.success) {
        setAccreditations(response.data.accreditations);
      } else {
        setError(response.data.message || 'Failed to fetch accreditations.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching accreditations.');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const resetForm = () => {
    setTitle('');
    setImage(null);
    setImagePreview(null);
    setShowForm(false);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    const formData = new FormData();
    formData.append('title', title);
    formData.append('image', image);

    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) {
        setError('Authentication required.');
        setSaving(false);
        return;
      }

      const response = await axios.post(`${apiUrl}/api/admin/accreditations`, formData, {
        headers: {
          Authorization: `Bearer ${sessionData.token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      if (response.data.success) {
        setSuccess('Accreditation added successfully.');
        resetForm();
        fetchAccreditations();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.message || 'Failed to add accreditation.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error adding accreditation.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this accreditation?')) {
      try {
        const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
        const response = await axios.delete(`${apiUrl}/api/admin/accreditations/${id}`, {
          headers: { Authorization: `Bearer ${sessionData.token}` }
        });
        if (response.data.success) {
          fetchAccreditations();
        } else {
          setError(response.data.message || 'Failed to delete accreditation.');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Error deleting accreditation.');
      }
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setAccreditations((items) => {
        const oldIndex = items.findIndex((item) => item.id.toString() === active.id);
        const newIndex = items.findIndex((item) => item.id.toString() === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex).map((item, index) => ({
          ...item,
          position: index + 1,
        }));
        updatePositions(newOrder);
        return newOrder;
      });
    }
  };

  const updatePositions = async (updatedAccreditations) => {
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      await axios.put(`${apiUrl}/api/admin/accreditations/order`, updatedAccreditations, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });
    } catch (err) {
      setError('Failed to update positions.');
      fetchAccreditations();
    }
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li>
                  <Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">
                    Home
                  </Link>
                </li>
                <li>
                  <span className="text-gray-500 fw-normal d-flex">
                    <i className="ph ph-caret-right" />
                  </span>
                </li>
                <li>
                  <span className="text-main-600 fw-normal text-15">Accreditations</span>
                </li>
              </ul>
            </div>
            <button
              type="button"
              className="btn btn-main rounded-pill py-9"
              onClick={() => { showForm ? resetForm() : setShowForm(true); }}
            >
              <i className={`ph ${showForm ? 'ph-x' : 'ph-plus'} me-2`} /> {showForm ? 'Cancel' : 'Add Accreditation'}
            </button>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          {showForm && (
            <div className="card mb-24">
              <div className="card-header border-bottom border-gray-100">
                <h5 className="mb-0">Add New Accreditation</h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="row g-20">
                    <div className="col-12">
                      <label className="h5 mb-8 fw-semibold font-heading d-block">
                        Image <span className="text-red-500">*</span>
                      </label>
                      <div className="d-flex align-items-center gap-16">
                        {imagePreview ? (
                          <img
                            src={imagePreview}
                            alt="preview"
                            style={{ width: 100, height: 80, borderRadius: 12, objectFit: 'cover', border: '3px solid #e8f5ee' }}
                          />
                        ) : (
                          <div style={{
                            width: 100, height: 80, borderRadius: 12, background: '#f3f4f6',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            border: '2px dashed #d1d5db'
                          }}>
                            <i className="ph ph-image text-gray-400" style={{ fontSize: 28 }} />
                          </div>
                        )}
                        <input
                          type="file"
                          className="form-control py-11"
                          accept="image/*"
                          onChange={handleImageChange}
                          required
                          style={{ maxWidth: 300 }}
                        />
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <label htmlFor="title" className="h5 mb-8 fw-semibold font-heading">
                        Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="title"
                        className="form-control py-11"
                        placeholder="e.g. IWFM"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        disabled={saving}
                        required
                      />
                    </div>
                    <div className="col-12 flex-align justify-content-end gap-8">
                      <button type="button" className="btn btn-outline-main rounded-pill py-9" onClick={resetForm}>
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-main rounded-pill py-9" disabled={saving}>
                        {saving ? 'Saving...' : 'Add Accreditation'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-header border-bottom border-gray-100 flex-between">
              <h5 className="mb-0">All Accreditations ({accreditations.length})</h5>
              <span className="text-13 text-gray-400">
                <i className="ph ph-arrows-out-cardinal me-1" /> Drag cards to reorder
              </span>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
              ) : accreditations.length === 0 ? (
                <div className="text-center py-40 text-gray-400">No accreditations yet. Add one above.</div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={accreditations.map((acc) => acc.id.toString())}
                    strategy={rectSortingStrategy}
                  >
                    <div className="row g-16">
                      {accreditations.map((acc) => (
                        <SortableItem
                          key={acc.id}
                          id={acc.id.toString()}
                          accreditation={acc}
                          onDelete={handleDelete}
                          apiUrl={apiUrl}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminAccreditations;
